import { useState } from "react"
import type { BuildStepCardData } from "../types"
import styles from "../styles/widget.module.css"
import { SizeSilhouetteGallery } from "./SizeSilhouetteGallery"
import { SprayerGallery } from "./SprayerGallery"
import { PaintConditionGallery } from "./PaintConditionGallery"
import { ClearcoatGallery } from "./ClearcoatGallery"

export function BuildStepCard({
  data,
  onChipClick,
}: {
  data: BuildStepCardData
  onChipClick: (chipLabel: string) => void
}) {
  const { partialSummary, nextStep } = data

  // Build a compact "what's chosen so far" subline. Color first, type pill,
  // then any axes already pinned.
  const chosenParts: string[] = [
    partialSummary.color,
    partialSummary.type === "peelable" ? "Peelable" : "Permanent",
  ]
  if (partialSummary.size) chosenParts.push(partialSummary.size)
  if (partialSummary.sprayer) chosenParts.push(`Sprayer: ${partialSummary.sprayer}`)
  if (partialSummary.finish) chosenParts.push(`Finish: ${partialSummary.finish}`)
  if (partialSummary.paintCondition) chosenParts.push(`Paint: ${partialSummary.paintCondition}`)
  if (partialSummary.clearcoat) chosenParts.push(`Clearcoat: ${partialSummary.clearcoat}`)

  const renders = partialSummary.colorRenders
  // At the finish-selection step, the customer is literally being asked
  // "matte or gloss" — so render BOTH side-by-side as a visual A/B when
  // we have both URLs. Lets them see the actual finish difference instead
  // of guessing from a chip label. Falls back to a single hero otherwise.
  const showFinishComparison =
    nextStep.axis === "finish" && !!renders?.matte && !!renders?.gloss
  const singleHeroSrc = renders?.matte ?? renders?.gloss

  return (
    <div className={styles.rc}>
      {/* On the FINISH step the two-up matte/gloss comparison is the
        * picker itself — render it INSIDE rc-body BELOW the title so the
        * customer reads what's being asked ("Pick a finish") before being
        * shown the visual options. Previously the BuildHeroPair sat at
        * the top of the card and the title appeared below the images,
        * which read backwards (image-first, context-after). Caught
        * 2026-06-03. Other axes (size/sprayer/paint/clearcoat) keep the
        * single BuildHero color-render thumbnail at the top so the
        * customer sees what color they're building. */}
      {!showFinishComparison && singleHeroSrc && (
        <BuildHero src={singleHeroSrc} colorName={partialSummary.color} />
      )}
      <div className={styles["rc-body"]}>
        <div className={styles["rc-sub"]}>Building your kit:</div>
        <div className={styles["rc-title"]}>{chosenParts.join(" · ")}</div>
        <div className={styles["rc-question"]} style={{ marginTop: 8 }}>{nextStep.question}</div>
        {showFinishComparison && renders ? (
          <BuildHeroPair
            matteSrc={renders.matte!}
            glossSrc={renders.gloss!}
            colorName={partialSummary.color}
            onSelect={onChipClick}
          />
        ) : nextStep.axis === "size" ? (
          <SizeSilhouetteGallery
            chips={nextStep.chips}
            priceDelta={nextStep.priceDelta}
            onSelect={onChipClick}
          />
        ) : nextStep.axis === "sprayer" ? (
          <SprayerGallery
            chips={nextStep.chips}
            priceDelta={nextStep.priceDelta}
            onSelect={onChipClick}
          />
        ) : nextStep.axis === "paintCondition" ? (
          <PaintConditionGallery
            chips={nextStep.chips}
            priceDelta={nextStep.priceDelta}
            onSelect={onChipClick}
          />
        ) : nextStep.axis === "clearcoat" ? (
          <ClearcoatGallery
            chips={nextStep.chips}
            priceDelta={nextStep.priceDelta}
            onSelect={onChipClick}
          />
        ) : (
          <div className={styles["rc-chips"]} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {nextStep.chips.map((c) => (
              <button
                key={c.value}
                type="button"
                className={styles["rc-chip"]}
                onClick={() => onChipClick(c.label)}
                title={c.helperText}
              >
                {c.label}
                {nextStep.priceDelta?.[c.value] != null && (
                  <span style={{ opacity: 0.7, marginLeft: 4 }}>
                    +${(nextStep.priceDelta[c.value] / 100).toFixed(0)}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** Soft studio-white backdrop shared by BuildHero and BuildHeroPair.
 *  Speed-shape renders are shot on light backgrounds — dark backdrops
 *  muddy the metallic flake and make the paint read wrong. A gentle
 *  off-white with a hint of cool depth keeps the dark colors (Shadow
 *  Black, Anthracite) legible without flattening the highlights. */
const HERO_BACKDROP =
  "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)"

/** Hero color render shared by BuildStepCard and BuildSummaryCard.
 *  Renders the speed-shape image with a graceful fallback if loading
 *  fails (network blip, deleted media, etc.) so the card never shows
 *  a broken-image icon. Color name pill overlays the bottom-left as
 *  a subtle in-image label — keeps the chrome tight. */
export function BuildHero({
  src,
  colorName,
}: {
  src: string
  /** Optional pill label. Omit to render the hero image without a label
   *  (BuildSummaryCard already carries variantTitle in the body text, no
   *  need to double-up). */
  colorName?: string
}) {
  const [failed, setFailed] = useState(false)
  // Shrunken hero — capped at 120px so the render takes ~20% of a 600px
  // canvas instead of dominating it. Object-fit `contain` so the whole
  // speed-shape image is visible (not cropped). Rounded corners + bottom
  // margin so it reads as a tidy thumbnail above the build-step body
  // rather than a full-bleed banner.
  return (
    <div
      style={{
        position: "relative",
        // Inset by 10px on each side so the hero aligns with the
        // kit-content tiles inside rc-body (which has 10px horizontal
        // padding). width: calc(100% - 20px) is needed because the
        // aspect-ratio + max-height combo would otherwise self-size
        // the box (160×120) instead of filling its parent. 12px TOP margin
        // so the white render container doesn't touch the top of the card.
        margin: "12px 10px 12px",
        width: "calc(100% - 20px)",
        maxHeight: 120,
        aspectRatio: "4 / 3",
        background: HERO_BACKDROP,
        overflow: "hidden",
        borderRadius: 14,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
      }}
    >
      {!failed && (
        <img
          src={src}
          alt={colorName ? `${colorName} color render` : "Color render"}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      )}
      {colorName && (
        <div
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            padding: "3px 9px",
            borderRadius: 999,
            background: "rgba(13,21,44,0.78)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.2,
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          {colorName}
        </div>
      )}
    </div>
  )
}

/** Two heroes side-by-side, labeled Satin and Ultra High Gloss. Renders
 *  on the finish-selection step so the customer can literally see the
 *  difference instead of guessing from a chip label. Each tile is a
 *  button — clicking it sends the corresponding chip label back to Pearl
 *  so the build advances. */
export function BuildHeroPair({
  matteSrc,
  glossSrc,
  colorName,
  onSelect,
}: {
  matteSrc: string
  glossSrc: string
  colorName: string
  /** Receives the chip label ("Satin" or "Ultra High Gloss") when the
   *  customer taps a tile. Wired through BuildStepCard to onChipClick
   *  which posts the label as the next user message. */
  onSelect: (chipLabel: string) => void
}) {
  return (
    <div style={{ margin: "0 10px 12px" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          opacity: 0.6,
          marginBottom: 10,
          textAlign: "center",
        }}
      >
        Tap a finish to pick
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <HeroTile
          src={matteSrc}
          label="Satin"
          colorName={colorName}
          onClick={() => onSelect("Satin")}
        />
        <HeroTile
          src={glossSrc}
          label="Ultra High Gloss"
          colorName={colorName}
          onClick={() => onSelect("Ultra High Gloss")}
        />
      </div>
    </div>
  )
}

function HeroTile({
  src,
  label,
  colorName,
  onClick,
}: {
  src: string
  label: string
  colorName: string
  onClick: () => void
}) {
  const [failed, setFailed] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Pick ${label} finish`}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        maxHeight: 150,
        background: HERO_BACKDROP,
        overflow: "hidden",
        borderRadius: 12,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
        border: "1px solid rgba(255,255,255,0.10)",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        transition:
          "border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.55)"
        e.currentTarget.style.boxShadow =
          "inset 0 0 0 1px rgba(255,255,255,0.6), 0 0 0 3px rgba(109,195,247,0.18)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"
        e.currentTarget.style.boxShadow = "inset 0 0 0 1px rgba(255,255,255,0.6)"
      }}
    >
      {!failed && (
        <img
          src={src}
          alt={`${colorName} — ${label}`}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "5px 8px",
          textAlign: "center",
          background:
            "linear-gradient(180deg, rgba(13,21,44,0) 0%, rgba(13,21,44,0.78) 100%)",
          color: "#fff",
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </button>
  )
}
